import axios, {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig
} from 'axios'
import { message, notification } from 'antd'
import { getApiConfig } from '@/config/api.config'

// 创建请求实例
const createRequest = (): AxiosInstance => {
    const config = getApiConfig()

    const instance = axios.create({
        baseURL: config.baseURL,
        timeout: config.timeout,
        withCredentials: config.withCredentials,
        headers: {
            // 'Content-Type': 'application/x-www-form-urlencoded', //form-data
            'Content-Type': 'application/json;charset=utf-8', //json
            'X-Requested-With': 'XMLHttpRequest'
        }
    })

    // 请求拦截器
    instance.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            // 添加 token
            const token = localStorage.getItem('token') || sessionStorage.getItem('token')

            if (token && config.headers) {
                // 支持多种 token 格式
                // config.headers.token = `Bearer ${token}`
                // 或者直接使用 token
                config.headers.token = token
                // 或者使用自定义 header 名称
                // config.headers['X-Access-Token'] = token
            }

            // 添加时间戳防止缓存
            if (config.method?.toLowerCase() === 'get' && config.params) {
                config.params._t = Date.now()
            }

            // 开发环境日志
            if (import.meta.env.DEV) {
                console.group(`🌐 请求: ${config.method?.toUpperCase()} ${config.url}`)
                console.log('请求参数:', config.params || config.data)
                console.log('请求头:', config.headers)
                console.groupEnd()
            }

            return config
        },
        (error) => {
            console.error('请求配置错误:', error)
            return Promise.reject(error)
        }
    )

    // 响应拦截器
    instance.interceptors.response.use(
        (response: AxiosResponse) => {
            // 如果是二进制数据流，直接返回
            if (response.config.responseType === 'blob') {
                return response.data
            }

            // 开发环境日志
            if (import.meta.env.DEV) {
                console.group(`✅ 响应: ${response.config.method?.toUpperCase()} ${response.config.url}`)
                console.log('响应状态:', response.status)
                console.log('响应数据:', response.data)
                console.groupEnd()
            }

            // 处理业务逻辑（根据后端返回格式调整）
            const { data, status } = response
            // 如果后端返回的是直接数据
            if (status === 200) {
                // 如果有特定的业务状态码
                if (data && typeof data === 'object' && 'code' in data) {
                    if (data.code === 200) {
                        return data
                    } else if(data.code === 401){
                        notification.error({
                            message: '未授权',
                            description: data.msg || '请先登录！',
                        })
                        // 跳转到首页，并删除token
                        setTimeout(() => {
                            sessionStorage.removeItem('token');
                            localStorage.removeItem('token');
                            localStorage.removeItem('userInfo');
                            sessionStorage.removeItem('userInfo');
                        }, 150)
                    } else {
                        // 业务错误
                        const errorMsg = data.msg || '业务错误'
                        message.error(errorMsg)
                        return Promise.reject(new Error(errorMsg))
                    }
                }
            }

            return data
        },
        (error) => {
            // 网络错误处理
            if (import.meta.env.DEV) {
                console.group(`❌ 请求错误: ${error.config?.method?.toUpperCase()} ${error.config?.url}`)
                console.error('错误详情:', error)
                console.groupEnd()
            }

            if (error.response) {
                const { status, data } = error.response
                switch (status) {
                    case 401:
                        notification.error({
                            message: '未授权',
                            description: '请重新登录',
                        })
                        // 跳转到首页，并删除token
                        setTimeout(() => {
                            window.location.href = '/';
                            sessionStorage.removeItem('token');
                            localStorage.removeItem('token');
                            localStorage.removeItem('userInfo');
                        }, 1500)
                        break

                    case 403:
                        notification.error({
                            message: '禁止访问',
                            description: '您没有权限访问此资源',
                        })
                        break

                    case 404:
                        notification.error({
                            message: '资源不存在',
                            description: '请求的资源不存在',
                        })
                        break

                    case 500:
                        notification.error({
                            message: '服务器错误',
                            description: '服务器内部错误，请稍后重试',
                        })
                        break

                    default:
                        // const errorMsg = data?.message || `请求失败 (${status})`
                        message.error(data?.msg || `请求失败 (${status})`)
                }
            } else if (error.request) {
                // 请求已发送但没有响应
                message.error('网络异常，请检查网络连接')
            } else {
                // 请求配置错误
                message.error('请求配置错误')
            }

            return Promise.reject(error)
        }
    )

    return instance
}

// 导出请求实例
export const request = createRequest()

// 常用的请求方法
export const http = {
    get: <T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> => {
        return request.get(url, { params, ...config })
    },

    post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        // 将数据转换为 FormData 格式
        const formData = new FormData()
        if (data && typeof data === 'object') {
            Object.keys(data).forEach(key => {
                const value = data[key]
                if (value !== null && value !== undefined) {
                    // 如果是文件对象，直接添加
                    if (value instanceof File || value instanceof Blob) {
                        formData.append(key, value)
                    } else if (Array.isArray(value)) {
                        // 如果是数组，遍历添加
                        value.forEach(item => {
                            formData.append(key, item)
                        })
                    } else if (typeof value === 'object') {
                        // 如果是对象，转为 JSON 字符串
                        formData.append(key, JSON.stringify(value))
                    } else {
                        // 其他类型直接转字符串
                        formData.append(key, String(value))
                    }
                }
            })
        }
        return request.post(url, formData, config)
    },

    put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        // 将数据转换为 FormData 格式
        const formData = new FormData()
        if (data && typeof data === 'object') {
            Object.keys(data).forEach(key => {
                const value = data[key]
                if (value !== null && value !== undefined) {
                    if (value instanceof File || value instanceof Blob) {
                        formData.append(key, value)
                    } else if (Array.isArray(value)) {
                        value.forEach(item => {
                            formData.append(key, item)
                        })
                    } else if (typeof value === 'object') {
                        formData.append(key, JSON.stringify(value))
                    } else {
                        formData.append(key, String(value))
                    }
                }
            })
        }
        return request.put(url, formData, config)
    },

    delete: <T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> => {
        return request.delete(url, { params, ...config })
    },

    upload: <T = any>(url: string, file: File, data?: any): Promise<T> => {
        const formData = new FormData()
        formData.append('file', file)

        if (data) {
            Object.keys(data).forEach(key => {
                formData.append(key, data[key])
            })
        }

        return request.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
    }
}

export default request